import { PlatformLocation } from '@angular/common';
import { AfterContentInit, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, distinctUntilChanged, filter, Subject, take, takeUntil } from 'rxjs';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { OnboardingStatus, SelectorOrganization, UserData } from '@tgx/shared/interfaces';
import {
  AuthService,
  CacheService,
  CheckForUpdatesService,
  GtmDataLayerService,
  PlatformAuthService,
  PlatformBrowserService,
  PlatformHeadService,
  PlatformInfoService,
  PlatformSelectorOrganizationService,
  UserDataService,
  APP_ENVIRONMENT,
} from '@tgx/shared/services';
import { isPublicRoutePath, PUBLIC_ROUTE_PATHS } from './public-route-paths';

@Component({
  selector: 'tgx-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, AfterContentInit, OnDestroy {
  private authService = inject(AuthService);
  private platformAuthService = inject(PlatformAuthService);
  private platformBrowserService = inject(PlatformBrowserService);
  private platformInfoService = inject(PlatformInfoService);
  private userDataService = inject(UserDataService);
  private modalService = inject(NgbModal);
  private platformLocation = inject(PlatformLocation);
  private gtm = inject(GtmDataLayerService);
  private router = inject(Router);
  private headService = inject(PlatformHeadService);
  private checkForUpdatesService = inject(CheckForUpdatesService);
  private platformSelectorOrganizationService = inject(PlatformSelectorOrganizationService);
  private cacheService = inject(CacheService);
  private environment = inject(APP_ENVIRONMENT);

  private destroy$ = new Subject<void>();
  private organizationSelected: SelectorOrganization | null = null;
  private isPublicRouteMode = false;
  private cachedRouteWasPublic = false;
  private userDependentSubscriptionsInitialized = false;
  private hasHydratedUserData = false;
  private hydrationPromise: Promise<void> | null = null;
  /**
   * Caches last emitted router event to prevent double emits
   */
  private cachedRouterEvent: RouterEvent;
  // Set true once the initial `gtm.pageview` has been emitted (by the service callback,
  // right after the initial `gtm.datalayer`). After that, navigations emit pageviews directly.
  private initialPageviewEmitted = false;
  private userData: UserData;

  protected showUrl = true;

  // Hides the generic browsing type in the new dashboard to show one custom
  protected dashboardBrowsingType = false;
  protected isLoading = true;
  protected hideBrowsingType = false;
  protected impersonationEmail: string;
  protected showOnboardingAlert = false;
  protected onboardingAlertPartnerType: 'Buyer' | 'Seller' | null = null;
  protected showBearerCopied = false;
  protected routeUrl: string;
  protected isLogged = false;

  ngOnInit(): void {
    if (!this.platformInfoService.isBrowser()) {
      this.hideBrowsingType = true;
    } else {
      // The service emits this right after the initial `gtm.datalayer`, guaranteeing the
      // datalayer-before-pageview order without dropping the pageview on hard loads.
      this.gtm.registerInitialPageview(() => {
        this.initialPageviewEmitted = true;
        this.gtm.pageTrack(this.resolveVirtualStatus());
      });
      this.initializeAnalyticsGuestFallback();
    }

    this.bootstrapForInitialRoute(this.resolveInitialPath());
  }

  /**
   * Pushes the single `gtm.datalayer` event as a guest (not logged) once the auth
   * state is resolved and the user turns out to be unauthenticated. When the user
   * is authenticated, the authenticated context is pushed from organization tracking.
   */
  private initializeAnalyticsGuestFallback(): void {
    combineLatest([this.platformAuthService.authStateResolved$, this.platformAuthService.userLoggedIn$])
      .pipe(
        filter(([isResolved]) => isResolved),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(([, isLoggedIn]) => {
        if (!isLoggedIn) {
          this.gtm.pushGuestContext(this.resolveVirtualStatus());
        }
      });
  }

  private resolveInitialPath(): string {
    if (typeof window === 'undefined') {
      return this.router.url;
    }

    const browserPath = window.location.pathname || '/';
    const routerPath = this.router.url || '/';

    // During hydration router.url can still be '/', while the browser URL is already the real target route.
    if (routerPath === '/' && browserPath !== '/') {
      return browserPath;
    }

    return routerPath;
  }

  private bootstrapForInitialRoute(initialPath: string): void {
    if (typeof window === 'undefined') {
      // Server render must stay side-effect free; browser auth flow runs after hydration.
      if (isPublicRoutePath(initialPath)) {
        this.showUrl = !this.isCompanyRegistrationPage(initialPath);
        this.showOnboardingAlert = false;
        this.routeUrl = initialPath;
      }
      return;
    }

    this.isPublicRouteMode = isPublicRoutePath(initialPath);
    this.cachedRouteWasPublic = this.isPublicRouteMode;

    if (this.isPublicRouteMode) {
      // Public pages must render immediately and must not trigger private auth bootstrap.
      this.showUrl = !this.isCompanyRegistrationPage(initialPath);
      this.showOnboardingAlert = false;
      this.hideBrowsingType = this.isStatusPage(initialPath) || !this.isLogged;
      this.routeUrl = initialPath;
      this.cachedRouteWasPublic = true;

      this.initializePublicRoutingTracking();

      // Validate session without forcing login and avoid saving public paths as redirect destinations.
      this.authService.handleAuthentication(false, PUBLIC_ROUTE_PATHS);

      this.platformInfoService.userAuthenticated$
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged((prev, curr) => !!prev === !!curr),
        )
        .subscribe(async (auth0Data) => {
          this.isLogged = !!auth0Data;
          if (!this.isLogged) {
            this.hasHydratedUserData = false;
          }
          this.hideBrowsingType = this.isStatusPage(this.routeUrl || initialPath) || !this.isLogged;

          // If user is logged in on a public route, initialize user and org data
          if (this.isLogged) {
            await this.ensureUserDataInitialized();
          }

          this.redirectPrivateForAuthenticatedUser();
        });
      return;
    }

    this.checkForUpdatesService.updatesPolling();

    (this.authService.handleAuthentication(true) as Promise<boolean>)
      .then(async (isAuthenticated) => {
        if (!isAuthenticated) {
          return;
        }

        await this.ensureUserDataInitialized();
      })
      .catch((error) => {
        console.error('APP_BOOTSTRAP: Private auth bootstrap failed', error);
      });

    this.platformInfoService.userLogged$.pipe(takeUntil(this.destroy$)).subscribe((userData) => {
      this.userData = userData;
      this.ensureUserDependentSubscriptionsInitialized();
    });
  }

  ngAfterContentInit(): void {
    if (!this.platformInfoService.isBrowser()) {
      return;
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(async () => {
        // Single source of navigation pageviews for both logged and guest flows. The initial
        // pageview is deferred until the `gtm.datalayer` is out (via emitInitialPageviewOnce),
        // so it never precedes the user context; subsequent navigations emit directly.
        this.trackNavigationPageview();

        const currentUrl = this.router.url;
        const isCurrentPublic = isPublicRoutePath(currentUrl);

        // Handle transition from public to private route: execute private bootstrap if needed
        if (this.cachedRouteWasPublic && !isCurrentPublic && this.isLogged) {
          this.checkForUpdatesService.updatesPolling();
          await this.ensureUserDataInitialized();
          this.ensureUserDependentSubscriptionsInitialized();
        }
        this.cachedRouteWasPublic = isCurrentPublic;

        if (isCurrentPublic) {
          this.isLoading = false;
          this.showUrl = !this.isCompanyRegistrationPage(currentUrl);
          this.showOnboardingAlert = false;
          this.hideBrowsingType = this.isStatusPage(currentUrl) || !this.isLogged;
          this.dashboardBrowsingType = false;
          return;
        }

        // Private route: always recompute hideBrowsingType so it recovers after visiting a public route
        this.hideBrowsingType = !this.isLogged || this.isStatusPage(currentUrl) || currentUrl.startsWith('/404');

        const isOnboardingPage = this.isOnboardingPage(currentUrl);

        if (isOnboardingPage) {
          this.showUrl = false;
          this.showOnboardingAlert = false;
        } else {
          this.showUrl = true;
          this.showOnboardingAlert = await this.checkProfile();
        }

        this.dashboardBrowsingType = currentUrl.includes('/dashboard');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected clearImpersonation(event: boolean): void {
    void this.authService.clearImpersonation(event);
  }

  protected async changeOrganizationType(): Promise<void> {
    if (!this.organizationSelected) {
      console.error('Cannot change organization type: no organization selected');
      return;
    }

    const profile = (await this.cacheService.retrieveProfilesByCodes([this.organizationSelected.code]))[0];
    const buyerStatus = profile?.buyerStatus ?? OnboardingStatus.None;

    // Buyer pending can be actively completed by the partner, so CTA only redirects.
    // Keep the current effective context untouched (no forced switcher/type change).
    if (buyerStatus === OnboardingStatus.Pending) {
      this.router.navigate(['/onboarding-implementation']);
      return;
    }

    this.organizationSelected.typeSelected = this.organizationSelected.typeSelected === 'seller' ? 'buyer' : 'seller';
    this.platformSelectorOrganizationService.generateUrl(true);
    this.platformInfoService.organizationSelected$.next(this.organizationSelected);
    this.platformBrowserService.setLocalStorageItem('organizationType', this.organizationSelected.typeSelected);
  }

  private initializeUserDependentSubscriptions(): void {
    // Tracks navigation events for analytics and SEO
    this.initializeRouterTracking();

    // Monitors organization changes and updates tracking services
    this.initializeOrganizationTracking();

    // Handles post-login actions and onboarding redirects
    this.initializeUserLoginTracking();

    // Shows loading spinner when external data is being fetched
    this.initializeExternalDataLoading();

    // Closes open modals when user navigates back via browser button
    this.initializeModalDismissal();
  }

  private ensureUserDependentSubscriptionsInitialized(): void {
    if (this.userDependentSubscriptionsInitialized) {
      return;
    }

    this.userDependentSubscriptionsInitialized = true;
    this.initializeUserDependentSubscriptions();
  }

  private async ensureUserDataInitialized(): Promise<void> {
    if (this.hasHydratedUserData) {
      return;
    }

    if (this.hydrationPromise) {
      await this.hydrationPromise;
      return;
    }

    this.hydrationPromise = (async () => {
      this.platformAuthService.setUserSessionData();
      await Promise.all([this.userDataService.initialize(), this.platformSelectorOrganizationService.initialize()]);
      this.hasHydratedUserData = true;

      // Push the authenticated `gtm.datalayer` (user context); the service emits the initial
      // `gtm.pageview` right after it (registered in ngOnInit), so the datalayer comes first.
      // Deduplicated by context signature if already emitted for the current organization.
      await this.gtm.pushAuthenticatedContext(this.platformInfoService.orgCode(), this.resolveVirtualStatus());
    })();

    try {
      await this.hydrationPromise;
    } finally {
      this.hydrationPromise = null;
    }
  }

  private initializeRouterTracking(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      const routerEvent: RouterEvent = (response as any).routerEvent ? (response as any).routerEvent : response;
      if (routerEvent instanceof NavigationEnd && this.cachedRouterEvent !== routerEvent) {
        this.cachedRouterEvent = routerEvent;
        this.headService.getSeoPagesData(routerEvent);
        this.hideBrowsingType =
          !this.isLogged || this.isStatusPage(routerEvent.url) || routerEvent.url.startsWith('/404'); // Hidden in SSR/non-logged states, on status and on 404.
      }
    });
  }

  private initializePublicRoutingTracking(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      const routerEvent: RouterEvent = (response as any).routerEvent ? (response as any).routerEvent : response;
      if (routerEvent instanceof NavigationEnd && this.cachedRouterEvent !== routerEvent) {
        this.cachedRouterEvent = routerEvent;
        this.routeUrl = routerEvent.urlAfterRedirects;
        this.headService.getSeoPagesData(routerEvent);
        this.redirectPrivateForAuthenticatedUser();
      }
    });
  }

  /**
   * Emits the pageview for a router navigation. The initial one is owned by the service
   * callback (fired right after the initial `gtm.datalayer`); subsequent navigations emit here.
   */
  private trackNavigationPageview(): void {
    if (!this.initialPageviewEmitted) return;
    this.gtm.pageTrack(this.resolveVirtualStatus());
  }

  /**
   * Resolves the virtual HTTP status for the active view by reading the deepest
   * `virtualStatus` route data (e.g. 404 for the wildcard/not-found route). Defaults to 200.
   */
  private resolveVirtualStatus(): number {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let virtualStatus = 200;

    while (snapshot) {
      const routeStatus = snapshot.data?.['virtualStatus'];
      if (typeof routeStatus === 'number') {
        virtualStatus = routeStatus;
      }
      snapshot = snapshot.firstChild;
    }

    return virtualStatus;
  }

  private redirectPrivateForAuthenticatedUser(): void {
    if (!this.isLogged || !this.platformInfoService.isBrowser()) {
      return;
    }

    // Only redirect company-registration (public onboarding); network/partners accessible for all.
    if (this.routeUrl?.startsWith('/company-registration')) {
      window.location.href = '/dashboard';
    }
  }

  private initializeOrganizationTracking(): void {
    this.platformInfoService.organizationSelected$.pipe(takeUntil(this.destroy$)).subscribe(async (response) => {
      this.organizationSelected = response;

      // Push the single authenticated `gtm.datalayer` event (logged + company_* vars).
      if (response?.code) {
        void this.gtm.pushAuthenticatedContext(response.code, this.resolveVirtualStatus());
      }

      // Read user email from memory-based user profile
      const userProfile = this.platformAuthService.getCurrentUserProfile();
      const userMail = userProfile?.email || this.userData?.email;
      const orgCode = response.code;
      const orgLabel = response.label;
      const orgType = response.typeSelected;

      const currentUrl = this.router.url;
      this.showOnboardingAlert = !this.isOnboardingPage(currentUrl) && (await this.checkProfile());

      if (this.environment === 'prod') {
        datadogRum.setUser({
          id: userMail,
          email: userMail,
        });
        datadogRum.setUserProperty('orgCode', orgCode);
        datadogRum.setUserProperty('orgLabel', orgLabel);
        datadogRum.setUserProperty('orgType', orgType);
      }

      datadogLogs.setUser({
        id: userMail,
        email: userMail,
      });
      datadogLogs.setUserProperty('orgCode', orgCode);
      datadogLogs.setUserProperty('orgLabel', orgLabel);
      datadogLogs.setUserProperty('orgType', orgType);
    });
  }

  private initializeUserLoginTracking(): void {
    this.platformAuthService.userLoggedIn$.pipe(takeUntil(this.destroy$)).subscribe({
      next: async (response) => {
        this.isLogged = !!response;
        this.impersonationEmail = this.platformAuthService.getCurrentImpersonationEmail();
        if (!this.isLogged) {
          this.hideBrowsingType = true;
        }

        if (response) {
          this.isLoading = false;
          await this.manageUserOnboardingRedirect();
        }
      },
      error: (error) => {
        console.error('Error in user login tracking:', error);
      },
    });
  }

  private initializeExternalDataLoading(): void {
    this.authService.loadingExternalData.pipe(takeUntil(this.destroy$)).subscribe((isLoadingResponse: boolean) => {
      if (isLoadingResponse) {
        this.isLoading = true;
      }
    });
  }

  /**
   * Dismisses any open modals when navigating back with the browser's back button
   */
  private initializeModalDismissal(): void {
    this.platformLocation.onPopState(() => {
      if (this.modalService.hasOpenModals()) this.modalService.dismissAll();
    });
  }

  private async checkProfile(): Promise<boolean> {
    if (!this.organizationSelected) {
      this.onboardingAlertPartnerType = null;
      return false;
    }

    const profile = (await this.cacheService.retrieveProfilesByCodes([this.organizationSelected.code]))[0];

    if (!profile) {
      console.error('No profiles found for code:', this.organizationSelected.code);
      this.onboardingAlertPartnerType = null;
      return false;
    }

    const buyerStatus = profile?.buyerStatus ?? OnboardingStatus.None;
    const sellerStatus = profile?.sellerStatus ?? OnboardingStatus.None;
    const isDualPartner = buyerStatus !== OnboardingStatus.None && sellerStatus !== OnboardingStatus.None;

    // Business decision: show banner only when Buyer is pending in dual profile and Seller is already complete.
    if (isDualPartner && buyerStatus === OnboardingStatus.Pending && sellerStatus === OnboardingStatus.Completed) {
      this.onboardingAlertPartnerType = 'Buyer';
      return true;
    }

    this.onboardingAlertPartnerType = null;
    return false;
  }

  private async manageUserOnboardingRedirect(): Promise<void> {
    const isUserOnboardingFinished = this.isUserOnboardingFinished();

    if (!isUserOnboardingFinished) {
      this.router.navigate(['onboarding-company']);
    }
  }

  private isUserOnboardingFinished(): boolean {
    return !this.userData?.onboardingProperties || this.userData?.onboardingProperties?.finishedAt !== null;
  }

  private isOnboardingPage(url: string): boolean {
    return url.includes('/onboarding-company') || url.includes('/onboarding-implementation') || url.includes('/mobile');
  }

  private isStatusPage(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return path === '/status' || path.startsWith('/status/');
  }

  private isCompanyRegistrationPage(url: string): boolean {
    return url.startsWith('/company-registration') || url.startsWith('/signup') || url.startsWith('/xml-redirect');
  }

  protected handleCopyBearer(copied: boolean) {
    this.showBearerCopied = copied;
  }
}
